/**
 * @fileoverview Shared run-flow upload step: import name/description, template
 *               picker, file drop zone, async preview parse, and parse preview.
 * @module import-editor/runFlow/steps/RunUploadStep
 *
 * Responsibilities:
 * - Render the "Template & File" step of the run flow: run metadata inputs,
 *   template select with shell/variant badges, optional geo mode autodetect
 *   + manual mode picker, optional GeoParquet spatial-filter slot, drag/drop
 *   upload zone, parse trigger with progress, sheet picker, and the parsed
 *   preview table.
 *
 * Dependencies:
 * - `../useImportRunFlow` — the shared state machine (via the `flow` prop).
 * - `../../utils/geoFileDetect` — geo file / GeoParquet detection.
 * - `lucide-react` icons.
 *
 * Architecture:
 * - Presentation-only composition over `useImportRunFlow`; all transitions
 *   (file select, parse, sheet change, template select) are hook handlers.
 * - Host-specific extras are opt-in: the manual mode picker and the spatial
 *   filter render only when the host enables them; the spatial filter UI
 *   itself is host-supplied via `renderSpatialFilter` so this package does
 *   not take a dependency on the host's selector component.
 *
 * Notes:
 * - Extracted from tenant-web's ImportWizardContainer (TemplateFileStep) and
 *   system-web's SystemImportWizardContainer upload step during the Phase 2A
 *   run-flow migration (G-002). Both hosts now share this single body.
 */

import { useRef, useState, type ReactNode } from 'react';
import { UploadCloud, FileText, History, X } from 'lucide-react';
import type { ImportRunFlow } from '../useImportRunFlow';
import type { SpatialFilterMode } from '../runFlowTypes';
import type { AvailableImportFile } from '../../unifiedWizard/services';
import { isGeoFile, isGeoParquetSource } from '../../utils/geoFileDetect';

/**
 * @contract SpatialFilterSlotProps
 *
 * Purpose:
 * - Props handed to the host-supplied spatial-filter renderer so the host's
 *   selector stays presentation-only and the run flow owns the state.
 */
export interface SpatialFilterSlotProps {
  filterMode: SpatialFilterMode;
  hazardLevels: string[];
  onFilterModeChange: (mode: SpatialFilterMode) => void;
  onHazardLevelsChange: (levels: string[]) => void;
}

/**
 * @contract RunUploadStepProps
 *
 * Purpose:
 * - Wire the upload step to the run-flow state machine plus host options.
 *
 * Fields:
 * - flow: the `useImportRunFlow` result.
 * - enableManualModePicker: show the geo autodetect badge + parcel/lookup
 *   picker (tenant-web).
 * - renderSpatialFilter: host renderer for the GeoParquet spatial filter.
 * - hideTemplatePicker: hide the template select (system-web deep-links
 *   into the wizard with a preselected template).
 * - fileInputId: DOM id for the hidden file input (E2E suites pin it).
 */
export interface RunUploadStepProps {
  flow: ImportRunFlow;
  enableManualModePicker?: boolean;
  renderSpatialFilter?: (props: SpatialFilterSlotProps) => ReactNode;
  hideTemplatePicker?: boolean;
  fileInputId: string;
}

/**
 * @component RunUploadStep
 *
 * Purpose:
 * - Render the run flow's Template & File step.
 *
 * Props:
 * - RunUploadStepProps.
 *
 * Behavior:
 * - File drops/picks clear prior parse state via `flow.handleFileSelect`.
 * - Parse runs the async upload → preview-job pipeline via `flow.handleParse`.
 * - When the host exposes `listAvailableImportFiles` (tenant-web), a second
 *   input mode renders next to the drop zone: "select previously imported
 *   file" lists persisted GeoParquet blobs lazily and selects one via
 *   `flow.handlePersistedFileSelect`, skipping upload entirely.
 *
 * Data Source:
 * - All state comes from the `useImportRunFlow` hook; no direct data access.
 *
 * Constraints:
 * - No API calls; no Dexie access. Host extras are slot/flag driven.
 *
 * Performance:
 * - Renders the preview table inline with overflow containment; no
 *   virtualization (preview is capped at the sample size).
 */
export default function RunUploadStep({
  flow,
  enableManualModePicker = false,
  renderSpatialFilter,
  hideTemplatePicker = false,
  fileInputId,
}: RunUploadStepProps) {
  const { state, patch, templates, loadingTemplates } = flow;
  const [dragDepth, setDragDepth] = useState(0);
  const isDragOver = dragDepth > 0;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Persisted-file picker state — the list loads lazily the first time the
  // user expands the "select previously imported file" panel.
  const [persistedOpen, setPersistedOpen] = useState(false);
  const [persistedFiles, setPersistedFiles] = useState<AvailableImportFile[] | null>(null);
  const [persistedLoading, setPersistedLoading] = useState(false);
  const [persistedError, setPersistedError] = useState<string | null>(null);

  const hasFileContext = !!state.file || !!state.filename;
  // A persisted selection has no File object — the converted blob URL is the
  // file context. Persisted files are always already-converted GeoParquet.
  const isPersistedSelection = !state.file && !!state.uploadResult?.blobUrl;
  const geoDetected = (state.file ? isGeoFile(state.file.name) : false) || isPersistedSelection;

  const togglePersistedPicker = async () => {
    const nextOpen = !persistedOpen;
    setPersistedOpen(nextOpen);
    if (!nextOpen || persistedFiles !== null || !flow.services.listAvailableImportFiles) return;
    setPersistedLoading(true);
    setPersistedError(null);
    try {
      const files = await flow.services.listAvailableImportFiles();
      setPersistedFiles(files);
    } catch (err: any) {
      setPersistedError(err?.message ?? 'Failed to load previously imported files');
    } finally {
      setPersistedLoading(false);
    }
  };
  const progressValue = flow.uploadProgress !== null
    ? Math.max(10, flow.uploadProgress)
    : flow.previewUiStage === 'queued'
      ? 45
      : flow.previewUiStage === 'processing'
        ? 78
        : flow.previewUiStage === 'parsed'
          ? 100
          : flow.previewUiStage === 'error'
            ? 100
            : null;
  const previewStageLabel = flow.uploadProgress !== null && flow.uploadProgress < 100
    ? 'Uploading directly to storage…'
    : flow.previewUiStage === 'queued'
      ? 'Preview job queued…'
      : flow.previewUiStage === 'processing'
        ? 'Parsing and sampling file…'
        : flow.previewUiStage === 'parsed'
          ? 'Preview complete'
          : flow.previewUiStage === 'error'
            ? 'Preview failed'
            : 'Ready to parse';

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Import Name <span className="text-red-500">*</span></label>
          <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
            placeholder="e.g. Parcel Update Q1 2026" value={state.importName}
            onChange={e => patch({ importName: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Description <span className="text-gray-400 font-normal">(optional)</span></label>
          <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
            placeholder="Brief note about this import run" value={state.importDescription}
            onChange={e => patch({ importDescription: e.target.value })} />
        </div>
      </div>

      {state.dataMode === 'parcelOwnerUpdate' && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Ownership data as-of date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            className="w-full max-w-sm border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-blue-500"
            value={state.dataAsOf}
            onChange={e => patch({ dataAsOf: e.target.value })}
          />
          <p className="mt-1 text-[11px] text-gray-500">
            The date this ownership data is current as of. Parcels whose recorded ownership
            is already this new or newer will be skipped — a stale file can never overwrite
            fresher data.
          </p>
        </div>
      )}

      {!hideTemplatePicker && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Import Template</label>
          {loadingTemplates ? <div className="text-xs text-gray-400">Loading templates…</div> : (
            <select className="w-full max-w-sm border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-700"
              value={state.selectedTemplate?._id ?? ''}
              onChange={e => flow.handleTemplateSelect(templates.find(x => x._id === e.target.value) ?? null)}>
              <option value="">— Select a template —</option>
              {templates.map(t => (
                <option key={t._id} value={t._id}>{t.name}{t.source === 'system' ? ' (System)' : ''}</option>
              ))}
            </select>
          )}
          {state.selectedTemplate && (() => {
            // A shell template has no persisted mappings. Variants carry
            // their own mapping even if derived from a shell, so the shell
            // test keys on both parentTemplateId AND an empty mapping count.
            const t = state.selectedTemplate;
            const isShell = !t.parentTemplateId && (t.fieldMappingCount ?? 0) === 0;
            return (
              <>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  {t.dataMode && (
                    <span className="bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5">{t.dataMode}</span>
                  )}
                  {t.importBehavior && (
                    <span className="bg-gray-100 text-gray-700 border border-gray-200 rounded-full px-2 py-0.5">{t.importBehavior}</span>
                  )}
                  {isShell ? (
                    <span className="bg-orange-50 text-orange-700 border border-orange-200 rounded-full px-2 py-0.5 font-medium">
                      Shell — map at import time
                    </span>
                  ) : (
                    <span className="bg-gray-100 text-gray-700 border border-gray-200 rounded-full px-2 py-0.5">
                      {t.fieldMappingCount} mappings
                    </span>
                  )}
                  {t.parentTemplateId && (
                    <span className="bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5">
                      Variant
                    </span>
                  )}
                </div>
                {isShell && (
                  <div className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
                    This is a <strong>shell template</strong>. Map the file's columns to the target fields on the next
                    step; the declared match-key target has been pre-seeded for you.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {enableManualModePicker && (state.file || isPersistedSelection) && (
        state.dataMode === 'parcel' && geoDetected ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-medium rounded-full px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              Parcel / Entity Data — detected from file type
            </span>
          </div>
        ) : !geoDetected && !state.dataMode ? (
          <div className="space-y-2">
            <label className="block text-xs font-semibold text-gray-700">Import Mode</label>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {(['parcel', 'lookup'] as const).map(m => (
                <button key={m} type="button"
                  onClick={() => patch({ dataMode: m })}
                  className={`border-2 rounded-lg px-4 py-3 text-left transition-colors ${
                    state.dataMode === m ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                  <div className="text-sm font-semibold text-gray-800">{m === 'parcel' ? 'Parcel / Entity' : 'Lookup Data'}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{m === 'parcel' ? 'Properties, buildings, parcels' : 'Code lists, reference tables'}</div>
                </button>
              ))}
            </div>
          </div>
        ) : null
      )}

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Data File</label>
        <div
          role="button"
          tabIndex={0}
          aria-label="File upload drop zone"
          className={[
            'relative rounded-lg border-2 border-dashed text-center cursor-pointer transition-all duration-150 select-none',
            hasFileContext
              ? 'py-2.5 px-3 flex items-center gap-3'
              : 'p-5 flex flex-col items-center gap-1.5',
            isDragOver
              ? 'border-blue-600 bg-blue-50 ring-4 ring-blue-200 scale-[1.01]'
              : hasFileContext
                ? 'border-green-400 bg-green-50 hover:border-green-500'
                : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/30',
          ].join(' ')}
          onDragEnter={e => {
            e.preventDefault();
            if (e.dataTransfer.types.includes('Files')) setDragDepth(d => d + 1);
          }}
          onDragOver={e => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={e => {
            e.preventDefault();
            setDragDepth(d => Math.max(0, d - 1));
          }}
          onDrop={e => {
            e.preventDefault();
            setDragDepth(0);
            flow.handleFileSelect(e.dataTransfer.files?.[0] ?? null);
          }}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          {hasFileContext ? (
            <>
              <FileText className={`h-5 w-5 shrink-0 ${isDragOver ? 'text-blue-600' : 'text-green-600'}`} />
              <div className="flex-1 min-w-0 text-left">
                <div className="text-xs font-semibold text-gray-800 truncate">
                  {state.file?.name ?? state.filename}
                  {isPersistedSelection && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-medium px-2 py-0.5 align-middle">
                      Previously converted
                    </span>
                  )}
                </div>
                {state.fileSize != null && (
                  <div className="text-[10px] text-gray-500">
                    {(state.fileSize / (1024 * 1024)).toFixed(1)} MB
                    {isDragOver && <span className="ml-2 font-medium text-blue-700">— release to replace</span>}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="shrink-0 inline-flex items-center gap-1 text-[11px] text-gray-600 hover:text-red-600 hover:underline"
                onClick={ev => {
                  ev.stopPropagation();
                  flow.handleFileSelect(null);
                  resetFileInput();
                }}
              >
                <X className="h-3 w-3" />
                Remove
              </button>
            </>
          ) : (
            <>
              <UploadCloud
                className={`h-7 w-7 transition-colors ${isDragOver ? 'text-blue-600' : 'text-gray-400'}`}
              />
              <div className={`text-sm font-semibold transition-colors ${isDragOver ? 'text-blue-700' : 'text-gray-700'}`}>
                {isDragOver ? 'Release to upload' : 'Drop a file here or click to browse'}
              </div>
              <div className="text-[11px] text-gray-500">
                GeoPackage · File Geodatabase · GeoJSON · Shapefile · CSV · XLSX · JSON
              </div>
            </>
          )}
          <input
            ref={fileInputRef}
            id={fileInputId}
            type="file"
            className="hidden"
            accept=".gpkg,.gdb,.geojson,.zip,.csv,.xlsx,.xls,.json"
            onChange={e => flow.handleFileSelect(e.target.files?.[0] ?? null)}
          />
        </div>

        {flow.supportsPersistedFiles && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => { void togglePersistedPicker(); }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-800 hover:underline"
            >
              <History className="h-3.5 w-3.5" />
              {persistedOpen ? 'Hide previously imported files' : 'Select a previously imported file'}
            </button>
            {persistedOpen && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 max-w-2xl">
                {persistedLoading && (
                  <div className="px-3 py-2.5 text-xs text-gray-500">Loading previously imported files…</div>
                )}
                {persistedError && (
                  <div className="px-3 py-2.5 text-xs text-red-600">{persistedError}</div>
                )}
                {!persistedLoading && !persistedError && (persistedFiles ?? []).length === 0 && (
                  <div className="px-3 py-2.5 text-xs text-gray-500">No previously imported files found.</div>
                )}
                {!persistedLoading && (persistedFiles ?? []).map(f => {
                  const selected = isPersistedSelection && state.uploadResult?.blobUrl === f.blobUrl;
                  return (
                    <button
                      key={f.blobUrl}
                      type="button"
                      onClick={() => flow.handlePersistedFileSelect(selected ? null : f)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left text-xs transition-colors ${
                        selected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <FileText className={`h-4 w-4 shrink-0 ${selected ? 'text-indigo-600' : 'text-gray-400'}`} />
                      <span className="flex-1 min-w-0 truncate font-medium text-gray-800">{f.filename}</span>
                      <span className="shrink-0 text-gray-500 tabular-nums">
                        {(f.sizeBytes / (1024 * 1024)).toFixed(1)} MB
                      </span>
                      <span className="shrink-0 text-gray-400 tabular-nums">
                        {f.lastModified ? new Date(f.lastModified).toLocaleDateString() : '—'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {renderSpatialFilter && hasFileContext && isGeoParquetSource(
        state.filename,
        state.sourceFormat,
        state.uploadResult?.fileType ?? state.parseResult?.fileType,
      ) && (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-700">Spatial Filter</label>
          <div className="text-[11px] text-gray-500 -mt-1">
            Choose which parcels to extract from this geo source. Applies to preview, dry-run, and commit.
          </div>
          <div className="max-w-2xl">
            {renderSpatialFilter({
              filterMode: state.filterMode,
              hazardLevels: state.hazardLevels ?? [],
              onFilterModeChange: m => patch({ filterMode: m }),
              onHazardLevelsChange: levels => patch({ hazardLevels: levels }),
            })}
          </div>
        </div>
      )}

      {hasFileContext && !state.parseResult && (
        <div className="flex flex-col gap-3">
          {progressValue !== null && (
            <div className="w-full rounded-lg border border-emerald-100 bg-emerald-50/80 px-3 py-2">
              <div className="flex items-center justify-between text-xs font-medium text-emerald-800">
                <span>{previewStageLabel}</span>
                <span>{progressValue}%</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-emerald-100">
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${progressValue}%` }} />
              </div>
            </div>
          )}
          {(state.file || isPersistedSelection) && (
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => flow.handleParse()} disabled={flow.parsing}
                className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-60">
                {flow.parsing ? 'Generating preview…' : 'Parse File'}
              </button>
              {flow.parsing && (
                <span className="text-xs text-gray-600">{previewStageLabel}</span>
              )}
              {flow.parseError && <span className="text-xs text-red-600">{flow.parseError}</span>}
            </div>
          )}
        </div>
      )}
      {flow.uploadError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-xs text-red-700 font-medium">Upload failed:</span>
          <span className="text-xs text-red-600">{flow.uploadError}</span>
        </div>
      )}

      {state.parseResult && (state.parseResult.sheets ?? []).length > 1 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <label className="text-xs font-semibold text-amber-800 shrink-0">Sheet:</label>
          <select
            className="border border-amber-300 rounded-md px-2 py-1 text-xs text-gray-800 bg-white focus:outline-hidden focus:ring-1 focus:ring-amber-400"
            value={state.selectedSheet ?? state.parseResult.activeSheet ?? ''}
            onChange={e => flow.handleSheetChange(e.target.value)}
            disabled={flow.parsing}
          >
            {(state.parseResult.sheets ?? []).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span className="text-[11px] text-amber-700">This file has multiple sheets — select which one to import.</span>
          {flow.parsing && <span className="text-[11px] text-amber-600 animate-pulse">Re-parsing…</span>}
        </div>
      )}

      {state.parseResult && (
        <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-hidden bg-white flex flex-col" style={{ minHeight: '320px' }}>
          <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between shrink-0">
            <span className="text-xs font-semibold text-gray-700">
              {state.parseResult.fileType.toUpperCase()} · {state.parseResult.columns.length} columns · {(state.parseResult.totalRows ?? 0).toLocaleString()} rows
              {state.parseResult.activeSheet && (
                <span className="ml-2 font-normal text-gray-500">· sheet: {state.parseResult.activeSheet}</span>
              )}
              {state.parseResult.totalRows != null && state.parseResult.sampleRows.length < state.parseResult.totalRows && (
                <span className="ml-2 font-normal text-amber-600">
                  · {state.parseResult.sampleRows.length} most-populated rows as preview (of {state.parseResult.totalRows.toLocaleString()})
                </span>
              )}
            </span>
            {state.parseResult.hasGeometry && (
              <span className="text-[11px] bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">Has geometry</span>
            )}
          </div>
          {state.parseResult.crsWarning && (
            <div className="px-3 py-2 border-b border-red-200 bg-red-50 text-xs text-red-700">
              {state.parseResult.crsWarning}
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="min-w-full text-xs text-left text-gray-700">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>{state.parseResult.columns.map(col => (
                  <th key={col} className="px-3 py-2 font-medium whitespace-nowrap">{col}</th>
                ))}</tr>
              </thead>
              <tbody>
                {state.parseResult.sampleRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 1 ? 'bg-gray-50/60' : ''}>
                    {state.parseResult!.columns.map(col => (
                      <td key={col} className="px-3 py-1.5 whitespace-nowrap max-w-[160px] truncate">{String(row[col] ?? '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t bg-gray-50 flex items-center justify-between text-[11px] text-gray-600 shrink-0">
            <span>{state.parseResult.sampleRows.length.toLocaleString()} preview rows</span>
            <span className="text-gray-400">Horizontal and vertical scrolling stay inside this grid.</span>
          </div>
        </div>
      )}
    </div>
  );
}
